import React, { useState } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import reportService, { EquipmentUsagePreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import YellowHeaderInfo from '../components/YellowHeaderInfo';
import { ServiceArea } from '../../../services/areasService';

interface EquipmentUsageReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateEquipmentUsage: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const EquipmentUsageReportView: React.FC<EquipmentUsageReportViewProps> = ({ 
  serviceAreas, 
  canCreateEquipmentUsage, 
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    startDate: '',
    endDate: ''
  });
  const [previewData, setPreviewData] = useState<EquipmentUsagePreviewData[]>([]); // Current page data
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {} // Will be overridden
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Server-side pagination helper
  const handlePageChange = (page: number, pageSize?: number) => {
    handleGeneratePreview(page, pageSize);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    handleGeneratePreview(1, newPageSize);
  };

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getEquipmentUsagePreviewData({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: pageSize || pagination.limit,
        sortField,
        sortDirection
      });

      // Store only current page data (server-side pagination)
      setPreviewData(response.data);
      
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        onPageChange: handlePageChange
      });
      
    } catch (error) {
      setErrorMessage('Failed to generate equipment usage report preview. Please try again.');
      setPreviewData([]);
      setPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        onPageChange: handlePageChange
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1); // Reset to page 1, fetch from server
  };

  const handleExport = async (customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateEquipmentUsageReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename,
        reportType
      });

      showSuccessToast('Equipment usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export equipment usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Generate default filename based on format: Equipment Usage - DATE
  const generateDefaultFilename = (reportType: 'simple' | 'sage-ready' = 'simple') => {
    let baseFilename = '';
    if (filters.endDate) {
      const date = new Date(filters.endDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Equipment Usage_${year}_${month}_${day}`;
    } else {
      // Fallback to current date
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Equipment Usage_${year}_${month}_${day}`;
    }
    
    // Append report type suffix
    const suffix = reportType === 'sage-ready' ? '_Sage' : '_Simple';
    return baseFilename + suffix;
  };

  const handleExportWithFilename = async (postedDate: string, customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateEquipmentUsageReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        customFilename,
        reportType
      });

      showSuccessToast('Equipment usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export equipment usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  const columns = [
    { 
      label: 'Date', 
      key: 'date',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Employee Code', 
      key: 'employeeCode',
      sortable: true,
      width: '160px'
    },
    { 
      label: 'Yard Activity Code', 
      key: 'yardActivityCode',
      sortable: true,
      width: '170px'
    },
    { 
      label: 'Activity Number', 
      key: 'activityNumber',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Vehicle Used', 
      key: 'vehicleUsed',
      sortable: true,
      width: '140px'
    },
    { 
      label: 'Vehicle Hours', 
      key: 'vehicleHours',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Vehicle Rate', 
      key: 'vehicleRate',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Finance Classification', 
      key: 'financeClassification',
      sortable: true,
      width: '180px',
      headerContent: (
        <div className="tw-flex tw-items-center">
          <span>Finance Classification</span>
          <i
            className="bi bi-info-circle tw-text-info tw-ml-1"
            style={{ fontSize: '0.75rem' }} 
            title="From project type when the task has a project; otherwise from task type (quantified, routine, overhead). Labels: Routine, Quantified, H200, Internal, Other Revenue. Sage-ready CSV uses EQ1–EQ5 in the finance column."
          ></i>
        </div>
      )
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview(1)}
        onExport={() => {}} // Remove export functionality from top
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true} // Disable export button in top bar
        generateButtonText="Run Report"
        hideExportButton={true} // Hide the export button entirely
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed} // Track collapse state
      >
        <FilterGrid>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters({ ...filters, serviceArea: e.target.value })}
              disabled={loadingServiceAreas}
            >
              <option value="">All Service Areas</option>
              {serviceAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Date Range</Form.Label>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{ width: '140px' }}
              />
              <span className="tw-text-content-muted tw-text-sm">to</span>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{ width: '140px' }}
              />
            </div>
          </Form.Group>
        </FilterGrid>
      </ReportParametersCard>
      </div>
      
      {/* Yellow Header Information */}
      <YellowHeaderInfo />
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0, // Important for flex child
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!canCreateEquipmentUsage}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - Scrollable */}
          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }}
            loading={loadingPreview}
            emptyMessage="No data available. Click 'Run Report' to load data."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed} // Pass collapse state to table
            yellowHeaderColumn="patrolDate" // Make only Patrol Date column yellow
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handleExport}
        onFinalExport={handleExportWithFilename}
        loadingPreliminary={loadingExport}
        loadingFinal={loadingExport}
        isEquipmentUsage={true}
        showFilenameInput={true}
        generateEquipmentUsageFilename={generateDefaultFilename}
      />

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default EquipmentUsageReportView;